import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { missionService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import MultilingualTextInput from '../components/MultilingualTextInput';
import MultilingualTextArea from '../components/MultilingualTextArea';
import { aUTCDelServidor } from '../utils/dates';

function Missions() {
  const [missions, setMissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState({
    title: { en: '', ca: '' },
    description: { en: '', ca: '' },
    story: { en: '', ca: '' },
    time_limit_minutes: 60,
    is_active: false
  });
  // La finestra de la missió, en hora d'aquí. Es converteix a UTC en desar.
  const [finestra, setFinestra] = useState({ inici: '', final: '' });
  const { isInstructor } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    fetchMissions();
  }, []);

  const fetchMissions = async () => {
    try {
      const response = await missionService.getMissions();
      setMissions(response.data);
    } catch (error) {
      toast.error(t('missions.errorFetchingMissions'));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleBilingualChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCreateMission = async (e) => {
    e.preventDefault();
    try {
      // La finestra i la casella «activa» viatgen amb la creació. Fins al
      // 06/08/2026 el formulari les enviava i el servidor les descartava en
      // silenci: la missió naixia sempre inactiva i sense finestra.
      const resposta = await missionService.createMission({
        ...formData,
        start_time: aUTCDelServidor(finestra.inici),
        end_time: aUTCDelServidor(finestra.final),
      });
      toast.success(t('missions.missionCreatedSuccess'));
      setShowCreateForm(false);
      setFormData({
        title: { en: '', ca: '' },
        description: { en: '', ca: '' },
        story: { en: '', ca: '' },
        time_limit_minutes: 60,
        is_active: false
      });
      setFinestra({ inici: '', final: '' });

      // Una missió acabada de crear no serveix de res: no té ni hipòtesis ni
      // evidències ni ningú assignat. S'entra directament a la seva pantalla,
      // que és on es fa tot això.
      const id = resposta?.data?.id;
      if (id) {
        navigate(`/missions/${id}`);
        return;
      }
      fetchMissions(); // Refresh the list
    } catch (error) {
      let message = t('missions.errorCreatingMission');
      if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          message = error.response.data.detail.map(err => err.msg).join(', ');
        } else {
          message = error.response.data.detail;
        }
      }
      toast.error(message);
    }
  };

  const handleArchiveMission = async (missionId, isArchived) => {
    try {
      if (isArchived) {
        await missionService.unarchiveMission(missionId);
        toast.success(t('missions.missionUnarchivedSuccess'));
      } else {
        await missionService.archiveMission(missionId);
        toast.success(t('missions.missionArchivedSuccess'));
      }
      fetchMissions(); // Refresh the list
    } catch (error) {
      toast.error(isArchived ? t('missions.errorUnarchivingMission') : t('missions.errorArchivingMission'));
    }
  };

  const handleDeleteMission = async (missionId, missionTitle) => {
    if (!window.confirm(t('missions.confirmDeleteMission', { title: missionTitle }))) {
      return;
    }

    try {
      await missionService.deleteMission(missionId);
      toast.success(t('missions.missionDeletedSuccess'));
      fetchMissions(); // Refresh the list
    } catch (error) {
      let message = t('missions.errorDeletingMission');
      if (error.response?.data?.detail) {
        message = error.response.data.detail;
      }
      toast.error(message);
    }
  };

  const handleToggleActive = async (missionId, currentStatus) => {
    try {
      await missionService.updateMission(missionId, { is_active: !currentStatus });
      toast.success(!currentStatus ? t('missions.missionActivatedSuccess') : t('missions.missionDeactivatedSuccess'));
      fetchMissions(); // Refresh the list
    } catch (error) {
      toast.error(!currentStatus ? t('missions.errorActivatingMission') : t('missions.errorDeactivatingMission'));
    }
  };

  if (loading) {
    return <div className="loading">{t('missions.loadingMissions')}</div>;
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--spacing-xl)' }}>
        <h1>{t('nav.missions')}</h1>
        {isInstructor && (
          <button
            className="btn btn-primary"
            onClick={() => setShowCreateForm(true)}
          >
            {t('missions.createMission')}
          </button>
        )}
      </div>

      {showCreateForm && (
        <div className="modal-overlay" onClick={() => setShowCreateForm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{t('missions.createNewMission')}</h2>
            <p className="field-description">{t('missions.createIntro')}</p>
            <form onSubmit={handleCreateMission}>
              <div className="form-group">
                <MultilingualTextInput
                  label={t('missions.title')}
                  value={formData.title}
                  onChange={(value) => handleBilingualChange('title', value)}
                  placeholder={{ en: 'Mission title', ca: 'Títol de la missió' }}
                  required
                />
              </div>
              <div className="form-group">
                <MultilingualTextArea
                  label={t('missions.description')}
                  value={formData.description}
                  onChange={(value) => handleBilingualChange('description', value)}
                  placeholder={{ en: 'Mission description', ca: 'Descripció de la missió' }}
                  rows={3}
                  required
                />
              </div>
              <div className="form-group">
                <MultilingualTextArea
                  label={t('missions.storyBackground')}
                  value={formData.story}
                  onChange={(value) => handleBilingualChange('story', value)}
                  placeholder={{ en: t('missions.storyPlaceholder'), ca: t('missions.storyPlaceholder') }}
                  rows={5}
                />
              </div>
              <div className="form-group">
                <label>{t('missions.timeLimitMinutes')}</label>
                <input
                  type="number"
                  name="time_limit_minutes"
                  value={formData.time_limit_minutes}
                  onChange={handleInputChange}
                  min="1"
                  required
                />
              </div>
              {/* La finestra: opcional, i les dues dates són independents.
                  Sense finestra, la missió és sempre oberta. */}
              <fieldset className="grup-finestra">
                <legend>{t('missions.window')}</legend>
                <p className="camp-ajuda">{t('missionDetail.windowHint')}</p>
                <div className="grup-finestra-camps">
                  <div className="form-group">
                    <label htmlFor="finestra-inici">{t('missionDetail.windowOpens')}</label>
                    <input
                      id="finestra-inici"
                      type="datetime-local"
                      value={finestra.inici}
                      onChange={(e) => setFinestra({ ...finestra, inici: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="finestra-final">{t('missionDetail.windowCloses')}</label>
                    <input
                      id="finestra-final"
                      type="datetime-local"
                      value={finestra.final}
                      onChange={(e) => setFinestra({ ...finestra, final: e.target.value })}
                    />
                  </div>
                </div>
              </fieldset>

              <div className="form-group">
                <label className="camp-casella">
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleInputChange}
                  />
                  <span>
                    {t('missions.activeStudentsCanAccess')}
                    <small className="camp-ajuda">{t('missions.activeHint')}</small>
                  </span>
                </label>
              </div>
              <div style={{ display: 'flex', gap: 'var(--spacing-md)', marginTop: 'var(--spacing-xl)' }}>
                <button type="submit" className="btn btn-primary">
                  {t('missions.createMission')}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowCreateForm(false)}
                >
                  {t('common.cancel')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {missions.length === 0 ? (
        <div className="card">
          <p>{t('missions.noMissionsAvailable')}</p>
        </div>
      ) : (
        <div className="missions-list">
          {missions.map((mission) => (
            <div key={mission.id} className="mission-card-horizontal">
              <div className="mission-content">
                <div className="mission-header">
                  <h3 className="mission-title">{mission.title}</h3>
                  <div className="mission-badges">
                    <span className="badge badge-secondary">
                      {mission.time_limit_minutes} min
                    </span>
                    <span className={`badge badge-${mission.is_active ? 'success' : 'warning'}`}>
                      {mission.is_active ? t('common.active') : t('common.inactive')}
                    </span>
                    {mission.archived && (
                      <span className="badge badge-secondary">
                        {t('common.archived')}
                      </span>
                    )}
                  </div>
                </div>
                <p className="mission-description">{mission.description}</p>
              </div>
              <div className="mission-actions">
                {/* La pantalla de la missió, només a l'instructor.
                    Per a ell és el formulari on edita el relat, les hipòtesis,
                    les evidències i la finestra. Per a l'alumne era una còpia de
                    la pantalla de l'exercici abans de començar —mateix relat,
                    mateix material, mateixes fitxes— i el feia llegir dues
                    vegades el mateix abans d'arribar on va. */}
                {isInstructor && (
                  <Link to={`/missions/${mission.id}`} className="icon-btn icon-btn-primary" title={t('missions.viewDetails')}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                  </Link>
                )}
                {!isInstructor && mission.is_active && !mission.archived && (
                  <Link to={`/matrix/${mission.id}`} className="btn btn-success">
                    {t('missions.startAnalysis')}
                  </Link>
                )}
                {isInstructor && (
                  <>
                    <button
                      onClick={() => handleToggleActive(mission.id, mission.is_active)}
                      className={`icon-btn ${mission.is_active ? 'icon-btn-warning' : 'icon-btn-success'}`}
                      title={mission.is_active ? t('missions.deactivateMission') : t('missions.activateMission')}
                    >
                      {mission.is_active ? (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="10"/>
                          <path d="M8 12l4 4 4-4"/>
                        </svg>
                      ) : (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="10"/>
                          <path d="M8 12l4-4 4 4"/>
                        </svg>
                      )}
                    </button>
                    <button
                      onClick={() => handleArchiveMission(mission.id, mission.archived)}
                      className={`icon-btn ${mission.archived ? 'icon-btn-secondary' : 'icon-btn-warning'}`}
                      title={mission.archived ? t('missions.unarchiveMission') : t('missions.archiveMission')}
                    >
                      {mission.archived ? (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="6,9 12,15 18,9"/>
                          <path d="M3 4h18v16H3z"/>
                        </svg>
                      ) : (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="21,8 21,21 3,21 3,8"/>
                          <rect x="1" y="3" width="22" height="5"/>
                          <line x1="10" y1="12" x2="14" y2="12"/>
                        </svg>
                      )}
                    </button>
                    <button
                      onClick={() => handleDeleteMission(mission.id, mission.title)}
                      className="icon-btn icon-btn-danger"
                      title={t('missions.deleteMission')}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3,6 5,6 21,6"/>
                        <path d="m19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2"/>
                        <line x1="10" y1="11" x2="10" y2="17"/>
                        <line x1="14" y1="11" x2="14" y2="17"/>
                      </svg>
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default Missions;